<template>
  <NuxtLinkLocale
    :to="eventLink"
    class="video-event-showcase-card"
    :class="{
      'video-event-showcase-card--mobile-active':
        mobileActiveCardIndex === cardIndex,
      'video-event-showcase-card--desktop-playing': isDesktopPlaying,
    }"
    :data-test-id="`video-event-showcase-card-${eventGetters.getPermalink(event)}`"
    @mouseenter="onCardMouseEnter"
    @mouseleave="onCardMouseLeave"
    @click="emit('click:video-event', cardIndex)"
  >
    <div v-if="portraitVideo" class="video-event-showcase-card__media">
      <NuxtImg
        v-if="posterSrc"
        :src="posterSrc"
        :alt="title"
        loading="lazy"
        class="video-event-showcase-card__poster"
      />
      <video
        :ref="bindVideoRef"
        class="video-event-showcase-card__video"
        :poster="posterSrc || undefined"
        muted
        playsinline
        preload="none"
        :controls="false"
        disablepictureinpicture
        controlslist="nodownload nofullscreen noremoteplayback noplaybackrate"
        :aria-label="title"
        @canplay="onMobileVideoCanPlay(cardIndex)"
      />
    </div>

    <div class="video-event-showcase-card__content">
      <div
        v-if="title"
        class="video-event-showcase-card__title"
        data-test-id="video-event-showcase-card-title"
      >
        {{ title }}
      </div>

      <ProductCardInformation
        :rating="rating"
        :is-partner-rating="isPartnerRating"
      />

      <div v-if="regularPrice" class="video-event-showcase-card__price">
        <div
          v-if="finalPrice"
          :class="{
            'video-event-showcase-card__price-discounted': originalPrice,
          }"
        >
          {{ finalPriceText }}
        </div>
        <div
          v-if="originalPrice"
          class="video-event-showcase-card__price-original"
        >
          {{ formatPriceAmount(Number(originalPrice), locale, 'EUR', 0) }}
        </div>
      </div>
    </div>
  </NuxtLinkLocale>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import { formatPriceAmount } from '~/utils/currencyHelpers'
import type { EventDescription } from '~/composables/types/event'
import { unbindEventVideoSource } from '~/composables/useEventVideoHlsBinding'
import {
  useMagazineVideoEventShowcasePlayback,
  VIDEO_SHOWCASE_CLIP_DURATION_MS,
} from '~/composables/magazine/useMagazineVideoEventShowcasePlayback'

const { event, cardIndex } = defineProps<{
  event: EventDescription
  cardIndex: number
}>()

const emit = defineEmits<{
  (e: 'click:video-event', index: number): void
}>()

const { locale, t } = useI18n()

const {
  mobileActiveCardIndex,
  registerMobileVideo,
  onDesktopCardEnter,
  onDesktopCardLeave,
  onMobileVideoCanPlay,
} = useMagazineVideoEventShowcasePlayback()

const portraitVideo = computed(() => eventGetters.getPortraitVideo(event))
const title = computed(() => eventGetters.getTitle(event))
const rating = computed(() => eventGetters.getReviewsRating(event))
const isPartnerRating = computed(() => eventGetters.getIsPartnerRating(event))
const regularPrice = computed(() => eventGetters.getRegularPriceAmount(event))
const specialPrice = computed(() => eventGetters.getSpecialPriceAmount(event))

const videoSources = computed(() => ({
  source: portraitVideo.value?.original || '',
  sourceStream: portraitVideo.value?.streaming_url,
}))

const posterSrc = computed(() => portraitVideo.value?.thumb || '')

const eventLink = computed(() => `/e/${eventGetters.getPermalink(event)}/`)

const finalPrice = computed(() =>
  Number(specialPrice.value || regularPrice.value)
)

const originalPrice = computed(() =>
  specialPrice.value ? regularPrice.value : undefined
)

const finalPriceText = computed(() =>
  t('product.card.price.priceText', {
    price: formatPriceAmount(finalPrice.value, locale.value, 'EUR', 0),
  })
)

const isDesktopPlaying = ref(false)
let videoElement: HTMLVideoElement | null = null
let desktopClipTimer: ReturnType<typeof setTimeout> | null = null

const clearDesktopClipTimer = () => {
  if (desktopClipTimer) {
    clearTimeout(desktopClipTimer)
    desktopClipTimer = null
  }
}

const bindVideoRef = (el: Element | ComponentPublicInstance | null) => {
  if (el instanceof HTMLVideoElement) {
    if (videoElement === el) return
    videoElement = el
    registerMobileVideo(cardIndex, el, videoSources.value)
    return
  }

  if (!el && videoElement) {
    registerMobileVideo(cardIndex, null, videoSources.value)
    videoElement = null
  }
}

const onCardMouseEnter = () => {
  if (!videoElement) return
  isDesktopPlaying.value = true
  onDesktopCardEnter(cardIndex, videoElement, videoSources.value)
  clearDesktopClipTimer()
  desktopClipTimer = setTimeout(() => {
    if (!videoElement) return
    isDesktopPlaying.value = false
    onDesktopCardLeave(cardIndex, videoElement)
  }, VIDEO_SHOWCASE_CLIP_DURATION_MS)
}

const onCardMouseLeave = () => {
  if (!videoElement) return
  clearDesktopClipTimer()
  isDesktopPlaying.value = false
  onDesktopCardLeave(cardIndex, videoElement)
}

onBeforeUnmount(() => {
  clearDesktopClipTimer()
  unbindEventVideoSource(videoElement)
  videoElement = null
})
</script>

<style lang="postcss" scoped>
.video-event-showcase-card {
  @apply flex flex-col gap-2 no-underline;
}

.video-event-showcase-card__media {
  @apply relative aspect-[9/16] w-full overflow-hidden rounded-lg bg-gray-200;
}

.video-event-showcase-card__poster,
.video-event-showcase-card__video {
  @apply absolute inset-0 h-full w-full object-cover;
}

.video-event-showcase-card__poster {
  z-index: 1;
  @apply transition-opacity duration-300;
}

.video-event-showcase-card__video {
  z-index: 0;
  pointer-events: none;

  &::-webkit-media-controls,
  &::-webkit-media-controls-enclosure,
  &::-webkit-media-controls-panel,
  &::-webkit-media-controls-play-button,
  &::-webkit-media-controls-start-playback-button,
  &::-webkit-media-controls-overlay-play-button,
  &::-webkit-media-controls-timeline,
  &::-webkit-media-controls-current-time-display,
  &::-webkit-media-controls-time-remaining-display,
  &::-webkit-media-controls-mute-button,
  &::-webkit-media-controls-volume-slider,
  &::-webkit-media-controls-toggle-closed-captions-button,
  &::-webkit-media-controls-full-screen-button {
    display: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
  }

  &::-moz-media-controls,
  &::-moz-media-controls-panel {
    display: none !important;
  }

  &::-ms-media-controls,
  &::-o-media-controls {
    display: none !important;
  }
}

@media (max-width: 639px) {
  .video-event-showcase-card--mobile-active .video-event-showcase-card__poster {
    @apply opacity-0 pointer-events-none;
  }

  .video-event-showcase-card:not(.video-event-showcase-card--mobile-active)
    .video-event-showcase-card__video {
    @apply invisible;
  }
}

@media (min-width: 640px) {
  .video-event-showcase-card--desktop-playing
    .video-event-showcase-card__poster {
    @apply opacity-0 pointer-events-none;
  }
}

.video-event-showcase-card__content {
  @apply flex flex-col gap-1;
}

.video-event-showcase-card__title {
  @apply text-base font-normal line-clamp-2;
}

.video-event-showcase-card__price {
  @apply flex flex-wrap items-center gap-2 text-sm;
}

.video-event-showcase-card__price-discounted {
  @apply font-semibold;
}

.video-event-showcase-card__price-original {
  @apply text-gray-500 line-through;
}
</style>
